import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportError } from '../lib/errors/reporter';

/**
 * Error boundary class component que captura errores sincronicos del render tree.
 *
 * <para>Diferencia vs el RootErrorBoundary del legacy: ese estaba diseñado para
 * `errorElement` de createBrowserRouter (data router). El monorepo usa
 * BrowserRouter + Routes declarativo (no data router), por lo que no hay
 * useRouteError disponible. La pieza que SÍ funciona y cubre el caso más común
 * (un throw sincrónico en cualquier componente) es el class-based ErrorBoundary
 * estándar de React.</para>
 *
 * <para>Mount: en main.tsx envolviendo la app entera, después del SnackbarProvider
 * pero fuera del BrowserRouter para que tampoco se rompa la navegación si la app
 * crashea.</para>
 *
 * Los errors capturados se mandan al reporter (batch + dedup) que sale al backend
 * via /api/errors/extension (mismo endpoint que el chrome extension).
 */
interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Reporter centraliza el envío al backend — batch + dedup + buckets.
    reportError(error, `react-error-boundary:${errorInfo.componentStack?.slice(0, 200) ?? 'unknown'}`);
  }

  private handleRetry = (): void => {
    // Hard reload — el state local de queries / context se resetea limpio.
    window.location.reload();
  };

  private handleHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onHome={this.handleHome}
        />
      );
    }
    return this.props.children;
  }
}

function ErrorFallback({
  error,
  onRetry,
  onHome,
}: {
  error: Error | null;
  onRetry: () => void;
  onHome: () => void;
}) {
  const { t } = useTranslation();
  const detail = error?.message || t('error.generic.detail');

  return (
    <Container
      maxWidth="sm"
      data-testid="error-boundary"
      sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}
    >
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Alert severity="error" variant="outlined">
          <Typography variant="h5" component="div" sx={{ mb: 1 }}>
            {t('error.generic.title', { status: '—' })}
          </Typography>
          <Typography sx={{
            color: "text.secondary"
          }}>{detail}</Typography>
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
          <Button variant="contained" onClick={onRetry} data-testid="error-retry">
            {t('error.retry')}
          </Button>
          <Button variant="outlined" onClick={onHome} data-testid="error-home">
            {t('error.home')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
